import React, { useState, Context, useEffect, useContext } from 'react'
import { QUERY_CUSTOMERS, CREATE_CUSTOMER, UPDATE_CUSTOMER, DELETE_CUSTOMER } from './graphql'
import { Customer, CreateCustomerVariables } from 'models'
import { useQuery, useMutation } from '@apollo/client'
import { useCurrentUser } from 'context/current-user'
import Notification from 'antd/es/notification'

export type State = {
  data: Customer[] | undefined
  loading: boolean
  refetch: any

  createCustomer: (customer: CreateCustomerVariables) => Promise<Customer>
  deleteCustomer: (_id: string) => Promise<boolean>
  updateCustomer: (customer: Customer) => Promise<boolean>
}

const shadowContext = React.createContext<State | undefined>(undefined)

export const context = shadowContext as Context<State>

export const useCustomers = () => useContext(context)

export const CustomersProvider = ({ children }: { children?: React.ReactNode }) => {
  const { currentUser } = useCurrentUser()

  const { loading, data, refetch } = useQuery<{ customers: Customer[] }>(QUERY_CUSTOMERS)
  const [_createMutation] = useMutation<{ createCustomer: Customer }, { customer: CreateCustomerVariables }>(CREATE_CUSTOMER)
  const [_deleteMutation] = useMutation<{ deleteCustomer: boolean }, { _id: string }>(DELETE_CUSTOMER)
  const [_updateMutation] = useMutation<{ updateCustomer: boolean }, { customer: Customer }>(UPDATE_CUSTOMER)

  const createCustomer = async (customer: CreateCustomerVariables): Promise<Customer> => {
    try {
      const { data, errors } = await _createMutation({ variables: { customer } })
      if (data?.createCustomer) {
        refetch()
        Notification.success({ message: 'Luotiin asiakas', placement: 'bottomLeft' })
        return data?.createCustomer
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Asiakasta ei voitu luoda ' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const updateCustomer = async (customer: Customer): Promise<boolean> => {
    try {
      const { data, errors } = await _updateMutation({ variables: { customer } })
      if (data?.updateCustomer) {
        refetch()
        Notification.success({ message: 'Päivitettiin asiakas', placement: 'bottomLeft' })
        return data?.updateCustomer
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Asiakasta ei voitu päivittää ' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const deleteCustomer = async (_id: string): Promise<boolean> => {
    try {
      const { data, errors } = await _deleteMutation({ variables: { _id } })
      if (data?.deleteCustomer) {
        refetch()
        Notification.success({ message: 'Poistettiin asiakas', placement: 'bottomLeft' })
        return data?.deleteCustomer
      } else {
        throw errors
      }
    } catch (e) {
      Notification.error({ message: 'Asiakasta ei voitu poista ' + JSON.stringify(e), placement: 'bottomLeft' })
      throw e
    }
  }

  const { Provider } = context

  return (
    <Provider
      value={{
        data: data?.customers,
        loading,
        refetch,

        createCustomer,
        updateCustomer,
        deleteCustomer
      }}
    >
      {children}
    </Provider>
  )
}
